import { Component, createSignal, onMount, onCleanup, For, Show } from 'solid-js';
import { selfServiceApi, RankingEntry, RankingResponse } from '../services/selfservice';

interface LiveRankingProps {
  sessionId: number;
  accessCode: string;
  onClose?: () => void;
}

const LiveRanking: Component<LiveRankingProps> = (props) => {
  const [ranking, setRanking] = createSignal<RankingEntry[]>([]);
  const [totalTiles, setTotalTiles] = createSignal(0);
  const [isFrozen, setIsFrozen] = createSignal(false);
  const [isLoading, setIsLoading] = createSignal(true);
  const [error, setError] = createSignal<string | null>(null);
  const [lastUpdate, setLastUpdate] = createSignal<Date | null>(null);

  let pollInterval: ReturnType<typeof setInterval> | null = null;

  const fetchRanking = async () => {
    try {
      const response = await selfServiceApi.getRanking(props.sessionId, props.accessCode);
      setRanking(response.ranking);
      setTotalTiles(response.total_tiles);
      setIsFrozen(response.is_frozen);
      setLastUpdate(new Date());
      setError(null);
    } catch (err: any) {
      console.error('Error fetching ranking:', err);
      if (err?.response?.status === 403) {
        setError('Live ranking nie jest dostepny dla tej sesji');
      } else {
        setError('Nie udalo sie pobrac rankingu');
      }
    } finally {
      setIsLoading(false);
    }
  };

  onMount(() => {
    fetchRanking();
    // Poll every 5 seconds
    pollInterval = setInterval(fetchRanking, 5000);
  });

  onCleanup(() => {
    if (pollInterval) {
      clearInterval(pollInterval);
    }
  });

  const getPositionIcon = (position: number): string => {
    switch (position) {
      case 1: return '🥇';
      case 2: return '🥈';
      case 3: return '🥉';
      default: return `${position}.`;
    }
  };

  const getProgressPercentage = (correct: number, total: number): number => {
    if (total === 0) return 0;
    return Math.round((correct / total) * 100);
  };

  return (
    <div class="live-ranking">
      <div class="ranking-header">
        <h3>
          <span class="ranking-icon">🏆</span>
          Live Ranking
        </h3>
        <Show when={props.onClose}>
          <button class="close-btn" onClick={props.onClose}>×</button>
        </Show>
      </div>

      <Show when={isFrozen()}>
        <div class="frozen-banner">
          <span class="frozen-icon">❄️</span>
          Ranking zamrozony przed koncem gry
        </div>
      </Show>

      <Show when={isLoading()}>
        <div class="loading-state">
          <div class="spinner"></div>
          <p>Ladowanie rankingu...</p>
        </div>
      </Show>

      <Show when={error()}>
        <div class="error-state">
          <span class="error-icon">⚠️</span>
          <p>{error()}</p>
        </div>
      </Show>

      <Show when={!isLoading() && !error() && ranking().length > 0}>
        <div class="ranking-table">
          <div class="ranking-header-row">
            <span class="col-position">#</span>
            <span class="col-team">Zespol</span>
            <span class="col-progress">Postep</span>
            <span class="col-penalty">Kary</span>
            <span class="col-time">Czas</span>
          </div>

          <For each={ranking()}>
            {(entry) => (
              <div
                class={`ranking-row ${entry.is_current_team ? 'current-team' : ''} ${entry.is_finished ? 'finished' : ''}`}
              >
                <span class="col-position">
                  {getPositionIcon(entry.position)}
                </span>
                <span class="col-team">
                  <span class="team-name">{entry.team_name}</span>
                  <Show when={entry.is_organizer}>
                    <span class="organizer-badge" title="Organizator">👑</span>
                  </Show>
                  <Show when={entry.is_current_team}>
                    <span class="current-badge" title="Twoj zespol">📍</span>
                  </Show>
                </span>
                <span class="col-progress">
                  <div class="progress-container">
                    <div
                      class="progress-bar"
                      style={{ width: `${getProgressPercentage(entry.correct_answers, entry.total_tiles)}%` }}
                    ></div>
                    <span class="progress-text">
                      {entry.correct_answers}/{entry.total_tiles}
                    </span>
                  </div>
                </span>
                <span class="col-penalty">
                  <Show when={entry.penalty_minutes > 0}>
                    <span class="penalty-badge">+{entry.penalty_minutes}min</span>
                  </Show>
                  <Show when={entry.penalty_minutes === 0}>
                    <span class="no-penalty">-</span>
                  </Show>
                </span>
                <span class="col-time">
                  <Show when={entry.is_finished && entry.game_time}>
                    <span class="finish-time">{entry.game_time}</span>
                    <span class="finish-icon">🏁</span>
                  </Show>
                  <Show when={!entry.is_finished}>
                    <span class="in-progress">W grze...</span>
                  </Show>
                </span>
              </div>
            )}
          </For>
        </div>

        <div class="ranking-footer">
          <Show when={lastUpdate()}>
            <span class="last-update">
              Ostatnia aktualizacja: {lastUpdate()?.toLocaleTimeString('pl-PL')}
            </span>
          </Show>
          <span class="auto-refresh">Odswiezanie co 5 sekund</span>
        </div>
      </Show>

      <Show when={!isLoading() && !error() && ranking().length === 0}>
        <div class="empty-state">
          <span class="empty-icon">📊</span>
          <p>Brak zespolow w rankingu</p>
        </div>
      </Show>

      <style>{`
        .live-ranking {
          background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
          border-radius: 12px;
          padding: 1.5rem;
          color: white;
          max-width: 600px;
          margin: 0 auto;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
        }

        .ranking-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 1rem;
          padding-bottom: 0.75rem;
          border-bottom: 1px solid rgba(255, 255, 255, 0.1);
        }

        .ranking-header h3 {
          margin: 0;
          font-size: 1.25rem;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .ranking-icon {
          font-size: 1.5rem;
        }

        .close-btn {
          background: none;
          border: none;
          color: rgba(255, 255, 255, 0.6);
          font-size: 1.5rem;
          cursor: pointer;
          padding: 0.25rem 0.5rem;
          line-height: 1;
          transition: color 0.2s;
        }

        .close-btn:hover {
          color: white;
        }

        .frozen-banner {
          background: linear-gradient(90deg, #00b4db, #0083b0);
          padding: 0.5rem 1rem;
          border-radius: 6px;
          margin-bottom: 1rem;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-size: 0.9rem;
        }

        .loading-state,
        .error-state,
        .empty-state {
          text-align: center;
          padding: 2rem;
        }

        .spinner {
          width: 40px;
          height: 40px;
          border: 3px solid rgba(255, 255, 255, 0.2);
          border-top-color: #4ade80;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin: 0 auto 1rem;
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        .error-state {
          color: #f87171;
        }

        .error-icon,
        .empty-icon {
          font-size: 2rem;
          display: block;
          margin-bottom: 0.5rem;
        }

        .ranking-table {
          border-radius: 8px;
          overflow: hidden;
        }

        .ranking-header-row {
          display: grid;
          grid-template-columns: 50px 1fr 120px 80px 100px;
          gap: 0.5rem;
          padding: 0.75rem;
          background: rgba(255, 255, 255, 0.1);
          font-weight: 600;
          font-size: 0.8rem;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          color: rgba(255, 255, 255, 0.7);
        }

        .ranking-row {
          display: grid;
          grid-template-columns: 50px 1fr 120px 80px 100px;
          gap: 0.5rem;
          padding: 0.75rem;
          border-bottom: 1px solid rgba(255, 255, 255, 0.05);
          transition: background 0.2s;
          align-items: center;
        }

        .ranking-row:hover {
          background: rgba(255, 255, 255, 0.05);
        }

        .ranking-row.current-team {
          background: rgba(74, 222, 128, 0.15);
          border-left: 3px solid #4ade80;
        }

        .ranking-row.finished {
          background: rgba(251, 191, 36, 0.1);
        }

        .ranking-row.current-team.finished {
          background: linear-gradient(90deg, rgba(74, 222, 128, 0.15), rgba(251, 191, 36, 0.1));
        }

        .col-position {
          font-weight: 700;
          font-size: 1.1rem;
          text-align: center;
        }

        .col-team {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-weight: 500;
        }

        .team-name {
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          max-width: 150px;
        }

        .organizer-badge,
        .current-badge {
          font-size: 0.9rem;
        }

        .progress-container {
          position: relative;
          background: rgba(255, 255, 255, 0.1);
          border-radius: 10px;
          height: 20px;
          overflow: hidden;
        }

        .progress-bar {
          position: absolute;
          top: 0;
          left: 0;
          height: 100%;
          background: linear-gradient(90deg, #4ade80, #22c55e);
          border-radius: 10px;
          transition: width 0.5s ease;
        }

        .progress-text {
          position: relative;
          z-index: 1;
          display: block;
          text-align: center;
          font-size: 0.75rem;
          line-height: 20px;
          font-weight: 600;
        }

        .penalty-badge {
          background: rgba(239, 68, 68, 0.3);
          color: #fca5a5;
          padding: 0.2rem 0.5rem;
          border-radius: 4px;
          font-size: 0.75rem;
          font-weight: 600;
        }

        .no-penalty {
          color: rgba(255, 255, 255, 0.4);
        }

        .col-time {
          display: flex;
          align-items: center;
          gap: 0.25rem;
          font-size: 0.9rem;
        }

        .finish-time {
          font-family: monospace;
          color: #fbbf24;
          font-weight: 600;
        }

        .finish-icon {
          font-size: 0.8rem;
        }

        .in-progress {
          color: rgba(255, 255, 255, 0.5);
          font-style: italic;
          font-size: 0.8rem;
        }

        .ranking-footer {
          margin-top: 1rem;
          padding-top: 0.75rem;
          border-top: 1px solid rgba(255, 255, 255, 0.1);
          display: flex;
          justify-content: space-between;
          font-size: 0.75rem;
          color: rgba(255, 255, 255, 0.5);
        }

        .auto-refresh {
          display: flex;
          align-items: center;
          gap: 0.25rem;
        }

        @media (max-width: 600px) {
          .live-ranking {
            padding: 1rem;
          }

          .ranking-header-row,
          .ranking-row {
            grid-template-columns: 40px 1fr 80px 60px;
            font-size: 0.8rem;
          }

          .col-time {
            display: none;
          }

          .ranking-header-row .col-time {
            display: none;
          }

          .team-name {
            max-width: 100px;
          }

          .progress-container {
            height: 16px;
          }

          .progress-text {
            font-size: 0.65rem;
            line-height: 16px;
          }
        }
      `}</style>
    </div>
  );
};

export default LiveRanking;
